// Keep live controls and effect clocks intact when another team's room state changes.
export function updateVaultBreakersDom(root: HTMLElement, markup: string): void {
    const template = root.ownerDocument.createElement("template");
    template.innerHTML = markup;
    const activeElement = root.ownerDocument.activeElement as HTMLElement | null;
    const element = (node: Node): Element | null => node.nodeType === 1 ? node as Element : null;
    const external = (node: Node): boolean => {
        const el = element(node);
        return Boolean(el?.classList.contains("vault-capy-runner-layer") || el?.classList.contains("vault-answer-cover") || el?.classList.contains("vault-category-zoom") || el?.classList.contains("vault-return-overlay"));
    };
    const key = (node: Node): string => {
        const el = element(node);
        if (!el) return node.nodeName;
        const markers = [...el.attributes].filter(attribute =>
            attribute.name === "id" || attribute.name === "data-score-popup-sequence" ||
            attribute.name === "data-vault-team-session" ||
            (attribute.name.startsWith("data-vault-") && attribute.name !== "data-vault-state") ||
            attribute.name === "data-roster-action-key"
        ).map(attribute => `${attribute.name}=${attribute.value}`).join("|");
        return `${el.nodeName}:${markers || el.classList.item(0) || ""}`;
    };
    const patch = (parent: Node, desired: Node): void => {
        const wantedKeys = new Set([...desired.childNodes].map(key));
        for (const node of [...parent.childNodes]) {
            if (!external(node) && !wantedKeys.has(key(node))) parent.removeChild(node);
        }
        const unused = new Set([...parent.childNodes].filter(node => !external(node)));
        let cursor = parent.firstChild;
        for (const next of [...desired.childNodes]) {
            const current = cursor && unused.has(cursor) && key(cursor) === key(next)
                ? cursor : [...unused].find(node => key(node) === key(next));
            if (!current) {
                parent.insertBefore(next.cloneNode(true), cursor);
                continue;
            }
            unused.delete(current);
            if (current !== cursor) parent.insertBefore(current, cursor);
            cursor = current.nextSibling;
            if (current.isEqualNode(next)) continue;
            const currentElement = element(current);
            const nextElement = element(next);
            if (currentElement && nextElement) {
                const currentHtml = currentElement as HTMLElement;
                const nextHtml = nextElement as HTMLElement;
                // Negative delays resume newly mounted effects. Retained effects
                // already advanced naturally, so keep their original start phase.
                if (currentHtml.style && nextHtml.style &&
                    currentElement.classList.contains("is-dismissing") === nextElement.classList.contains("is-dismissing")) {
                    for (const property of Array.from({ length: currentHtml.style.length }, (_, index) => currentHtml.style.item(index))) {
                        if ((property.startsWith("--vault-") && property.endsWith("-delay") && nextHtml.style.getPropertyValue(property)) ||
                            property.startsWith("--player-roster-")) {
                            nextHtml.style.setProperty(property, currentHtml.style.getPropertyValue(property));
                        }
                    }
                }
                for (const name of [...currentElement.classList]) {
                    if (/^player-roster-(columns-|space-|fills-height|needs-scroll)/.test(name)) nextElement.classList.add(name);
                }
                const pressing = currentElement.classList.contains("is-pressing") && !nextElement.hasAttribute("disabled");
                const hasRunner = currentElement.classList.contains("has-capy-runner");
                for (const attribute of [...currentElement.attributes]) {
                    if (!nextElement.hasAttribute(attribute.name)) currentElement.removeAttribute(attribute.name);
                }
                for (const attribute of [...nextElement.attributes]) {
                    if (currentElement.getAttribute(attribute.name) !== attribute.value) {
                        currentElement.setAttribute(attribute.name, attribute.value);
                    }
                }
                if (pressing) currentElement.classList.add("is-pressing");
                if (hasRunner) currentElement.classList.add("has-capy-runner");
                // Let a teacher finish typing through passive room updates.
                if (currentElement.tagName === "INPUT" && currentElement !== activeElement) {
                    (currentElement as HTMLInputElement).value = (nextElement as HTMLInputElement).value;
                }
                if (!currentElement.hasAttribute("data-vault-player-waiting-capy")) patch(current, next);
            } else if (current.nodeValue !== next.nodeValue) {
                current.nodeValue = next.nodeValue;
            }
        }
        for (const node of unused) parent.removeChild(node);
    };
    patch(root, template.content);
    if (activeElement?.isConnected && root.contains(activeElement) && root.ownerDocument.activeElement !== activeElement) {
        activeElement.focus({ preventScroll: true });
    }
}
